import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as zod from "zod";

export const Route = createFileRoute('/test')({
  component: Payment,
})

const IsNotEmptyString = (message: string) => zod.string({ message }).min(1, { message })

const getDefaultFormState = (type: PaymentMethodEnum) => {
  return defaultValue.find(
    (item) => item.type === type
  ) as PaymentMethodSchemaType;
};

enum PaymentMethodEnum {
  CREDIT_CARD = "creditCard",
  PAYPAL = "paypal",
  BANKTRANSFER = "bankTransfer"
}

const creditCardSchema = zod.object({
  type: zod.literal(PaymentMethodEnum.CREDIT_CARD),
  cardNumber: zod.string().regex(/^\d{16}$/, "The card number must contain 16 digits"),
  cvv: zod.string().regex(/^\d{3}$/, "The Card Validation Code must contain 3 digits"),
})

const paypalSchema = zod.object({
  type: zod.literal(PaymentMethodEnum.PAYPAL),
  email: zod.string().email("PayPal email is invalid"),
})

const bankTransferSchema = zod.object({
  type: zod.literal(PaymentMethodEnum.BANKTRANSFER),
  accountNumber: IsNotEmptyString("The account number must contain at least 10 characters"),
  bankCode: IsNotEmptyString("The bank code must contain at least 4 characters")
})

const paymentMethodSchema = () => zod.discriminatedUnion("type", [
  creditCardSchema, paypalSchema, bankTransferSchema
]);

type PaymentMethodSchemaType = zod.infer<ReturnType<typeof paymentMethodSchema>>


const bankTransferDefault: Extract<
  PaymentMethodSchemaType,
  { type: PaymentMethodEnum.BANKTRANSFER }
> = {
  accountNumber: '',
  bankCode: '',
  type: PaymentMethodEnum.BANKTRANSFER,
};

const paypalDefault: Extract<
  PaymentMethodSchemaType,
  { type: PaymentMethodEnum.PAYPAL }
> = {
  email: '',
  type: PaymentMethodEnum.PAYPAL,
};

const creditCardDefault: Extract<
  PaymentMethodSchemaType,
  { type: PaymentMethodEnum.CREDIT_CARD }
> = {
  cardNumber: '',
  cvv: '',
  type: PaymentMethodEnum.CREDIT_CARD,
};

const defaultValue = [creditCardDefault, paypalDefault, bankTransferDefault];


function Payment() {
  const form = useForm<PaymentMethodSchemaType>({
    resolver: zodResolver(paymentMethodSchema()),
    defaultValues: {
      type: PaymentMethodEnum.CREDIT_CARD,
    },
  });

  const { register, handleSubmit } = form;

  const paymentType = form.watch().type;

  const handleChangePaymentType = (type: PaymentMethodEnum) => {
    form.setValue('type', type);
  };

  const handleResetForm = () => {
    form.reset(getDefaultFormState(paymentType));
  };

  const onSubmit: SubmitHandler<PaymentMethodSchemaType> = (data) => {
    console.log('data', data);
  };

  const PaymentTypeFormNode: React.ReactNode = (() => {
    switch (paymentType) {
      case PaymentMethodEnum.BANKTRANSFER:
        return (
          <div>
            <div className="form">
              <label>Account number</label>
              <input
                {...register('accountNumber')}
                placeholder="Enter your account number"
              />
            </div>

            <div className="form">
              <label>Bank code</label>
              <input
                {...register('bankCode')}
                placeholder="Enter your bank code"
              />
            </div>
          </div>
        );

      case PaymentMethodEnum.CREDIT_CARD:
        return (
          <div>
            <div className="form">
              <label>Card Number</label>
              <input
                {...register('cardNumber')}
                placeholder="Enter your card number"
              />
            </div>

            <div className="form">
              <label>CVV</label>
              <input
                {...register('cvv')}
                placeholder="Enter your card validation code"
              />
            </div>
          </div>
        );

      case PaymentMethodEnum.PAYPAL:
        return (
          <div className="form">
            <label>Email</label>
            <input
              type="email"
              {...register('email')}
              placeholder="Enter your email"
            />
          </div>
        );

      default:
        throw new Error(
          'Exhaustive Guard Error : received value' + paymentType
        );
    }
  })();

  return (
    <form className="form-wrapper" onSubmit={handleSubmit(onSubmit)}>
      <div className="form" style={{ marginBottom: '1rem' }}>
        <label>Type de paiement</label>
        <select
          value={paymentType}
          onChange={(e) =>
            handleChangePaymentType(e.target.value as PaymentMethodEnum)
          }
          style={{ display: 'block', marginTop: '0.5rem' }}
        >
          <option value="creditCard">Credit Card</option>
          <option value="paypal">PayPal</option>
          <option value="bankTransfer">Bank Transfer</option>
        </select>

        <span className="icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="6"
            viewBox="0 0 10 6"
          >
            <path
              id="Tracé_532"
              data-name="Tracé 532"
              d="M295.717,353.232a.725.725,0,0,1,1.063,0,.8.8,0,0,1,.163.256.834.834,0,0,1,0,.606.8.8,0,0,1-.163.256l-4.248,4.418a.726.726,0,0,1-1.064,0l-4.248-4.418a.8.8,0,0,1-.163-.256.834.834,0,0,1,0-.606.8.8,0,0,1,.163-.256.725.725,0,0,1,1.063,0L292,356.856Z"
              transform="translate(-287 -353)"
              fill="#333"
            />
          </svg>
        </span>
      </div>

      {PaymentTypeFormNode}

      <div style={{ display: 'flex', gap: 20 }}>
        <button type="button" onClick={handleResetForm}>
          Cancel
        </button>
        <button type="submit" className="submit-btn">
          Submit
        </button>
      </div>
    </form>
  );
};